/**
 * API client for the Developer Console
 *
 * Handles authentication and API calls to console-specific endpoints.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

// FastAPI validation error format
export interface ValidationError {
  loc: (string | number)[]
  msg: string
  type: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>,
    public validationErrors?: ValidationError[]
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /**
   * Get a formatted message for a specific field's validation error
   */
  getFieldError(fieldName: string): string | undefined {
    if (!this.validationErrors) return undefined
    const error = this.validationErrors.find(e =>
      e.loc.includes(fieldName) || e.loc[e.loc.length - 1] === fieldName
    )
    return error?.msg
  }

  /**
   * Get all field errors as a map
   */
  getFieldErrors(): Record<string, string> {
    if (!this.validationErrors) return {}
    return this.validationErrors.reduce((acc, error) => {
      // Get the field name (last element in loc, typically after "body")
      const field = String(error.loc[error.loc.length - 1])
      acc[field] = error.msg
      return acc
    }, {} as Record<string, string>)
  }

  /**
   * Get a user-friendly summary of all validation errors
   */
  getValidationSummary(): string {
    if (!this.validationErrors || this.validationErrors.length === 0) {
      return this.message
    }
    return this.validationErrors
      .map(e => {
        const field = String(e.loc[e.loc.length - 1])
        // Capitalize and format field name
        const formattedField = field.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
        return `${formattedField}: ${e.msg}`
      })
      .join('\n')
  }
}

// Token storage
let accessToken: string | null = null
let refreshToken: string | null = null

export function setTokens(access: string | null, refresh: string | null) {
  accessToken = access
  refreshToken = refresh
  if (access) {
    localStorage.setItem('console_access_token', access)
  } else {
    localStorage.removeItem('console_access_token')
  }
  if (refresh) {
    localStorage.setItem('console_refresh_token', refresh)
  } else {
    localStorage.removeItem('console_refresh_token')
  }
}

export function getTokens() {
  if (!accessToken) {
    accessToken = localStorage.getItem('console_access_token')
  }
  if (!refreshToken) {
    refreshToken = localStorage.getItem('console_refresh_token')
  }
  return { accessToken, refreshToken }
}

export function clearTokens() {
  accessToken = null
  refreshToken = null
  localStorage.removeItem('console_access_token')
  localStorage.removeItem('console_refresh_token')
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { skipAuth?: boolean }
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  const { accessToken: token } = getTokens()
  if (token && !options?.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include', // Send cookies for session auth
  })

  if (!response.ok) {
    // Handle 401 - try to refresh token
    if (response.status === 401 && !options?.skipAuth) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        // Retry the request with new token
        return request<T>(method, path, body, options)
      }
      // Clear tokens and throw
      clearTokens()
    }

    let errorData: {
      code?: string
      message?: string
      detail?: string | ValidationError[]
      details?: Record<string, unknown>
    } = {}
    try {
      errorData = await response.json()
    } catch {
      // Ignore JSON parse errors
    }

    // Handle FastAPI 422 validation errors
    let validationErrors: ValidationError[] | undefined
    let message = errorData.message || `Request failed with status ${response.status}`

    if (response.status === 422 && errorData.detail) {
      if (Array.isArray(errorData.detail)) {
        validationErrors = errorData.detail
        // Create a user-friendly message from validation errors
        message = validationErrors.length === 1
          ? `Validation error: ${validationErrors[0].msg}`
          : `Validation errors: ${validationErrors.length} issues found`
      } else if (typeof errorData.detail === 'string') {
        message = errorData.detail
      }
    }

    throw new ApiError(
      response.status,
      errorData.code || 'VALIDATION_ERROR',
      message,
      errorData.details,
      validationErrors
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

async function tryRefreshToken(): Promise<boolean> {
  const { refreshToken: refresh } = getTokens()
  if (!refresh) return false

  try {
    const response = await fetch(`${API_URL}/console/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
      credentials: 'include',
    })

    if (!response.ok) return false

    const data = await response.json()
    setTokens(data.access_token, data.refresh_token)
    return true
  } catch {
    return false
  }
}

// HTTP helpers
const get = <T>(path: string) => request<T>('GET', path)
const post = <T>(path: string, body?: unknown, options?: { skipAuth?: boolean }) =>
  request<T>('POST', path, body, options)
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body)
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body)
const del = <T>(path: string, body?: unknown) => request<T>('DELETE', path, body)

// File upload helper
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)

  const headers: HeadersInit = {}
  const { accessToken: token } = getTokens()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    let errorData: { code?: string; message?: string; details?: Record<string, unknown> } = {}
    try {
      errorData = await response.json()
    } catch {
      // Ignore JSON parse errors
    }
    throw new ApiError(
      response.status,
      errorData.code || 'UPLOAD_ERROR',
      errorData.message || `Upload failed with status ${response.status}`,
      errorData.details
    )
  }

  return response.json()
}

// ============================================================================
// Types - Developer
// ============================================================================

export interface Developer {
  id: string
  email: string
  email_verified: boolean
  display_name: string | null
  avatar_url: string | null
  has_password: boolean
  mfa_enabled: boolean
  created_at: string
}

export interface Session {
  id: string
  device_name: string | null
  device_type: string
  ip_address: string
  location: string | null
  user_agent: string | null
  last_active_at: string
  created_at: string
  is_current: boolean
}

export interface MFAFactor {
  id: string
  type: 'totp' | 'passkey'
  name: string | null
  created_at: string
  last_used_at: string | null
}

export interface TOTPSetupResponse {
  secret: string
  uri: string
  qr_code: string
}

export interface LinkedIdentity {
  id: string
  provider: 'google' | 'github' | 'discord' | 'twitter' | 'telegram'
  provider_user_id: string
  email: string | null
  name: string | null
  created_at: string
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  developer: Developer
  requires_mfa?: boolean
  mfa_token?: string
}

export interface RegisterData {
  email: string
  password: string
  display_name?: string
}

export interface LoginData {
  email: string
  password: string
}

export interface MFAVerifyData {
  mfa_token: string
  code: string
}

// ============================================================================
// Types - Organization
// ============================================================================

export interface OrgInfo {
  id: string
  name: string
  slug: string
  description: string | null
  logo_url: string | null
  banner_logo_url: string | null
  website_url: string | null
  support_email?: string | null
  status: string
  created_at: string
  updated_at: string
}

export interface OrgRoleInfo {
  id: string
  slug: string
  name: string
  description: string | null
  scopes: string[]
  is_system: boolean
  created_at: string
}

export interface OrgWithRoleInfo {
  organization: OrgInfo
  role: OrgRoleInfo
  joined_at: string | null
}

// Backend ConsoleOrgWithRole type (matches backend schema)
export interface ConsoleOrgWithRole {
  org: OrgInfo
  role: string  // 'owner' | 'admin' | 'developer' | 'billing' | 'viewer'
  scopes: string[]
  joined_at: string
}

export interface OrgDetailResponse {
  org: OrgInfo
  role: string
  scopes: string[]
  developer_count: number
  app_count: number
}

export interface OrgMemberInfo {
  id: string
  user_id: string
  email: string | null
  display_name: string | null
  avatar_url: string | null
  role: OrgRoleInfo
  status: string
  joined_at: string | null
  created_at: string
}

export interface PendingInviteInfo {
  id: string
  email: string
  role: OrgRoleInfo
  invited_by: string | null
  created_at: string
  expires_at: string | null
}

// Backend DeveloperInfo type (from console_org schema)
export interface DeveloperInfo {
  id: string
  role_id: string
  developer_id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role: string  // 'owner' | 'admin' | 'developer' | 'billing' | 'viewer'
  scopes: string[]
  created_at: string
}

// Backend InviteInfo type
export interface InviteInfo {
  id: string
  email: string
  role: string
  status: string
  invited_by: string | null
  created_at: string
  expires_at: string
}

// Invite validation types
export interface ValidateInviteDetails {
  org_slug: string
  org_name: string
  org_logo_url: string | null
  email: string
  role: string
  invited_by: string | null
  expires_at: string
}

export interface ValidateInviteResponse {
  valid: boolean
  error: string | null // "not_found", "expired", "already_used", "org_inactive"
  details: ValidateInviteDetails | null
}

export interface AcceptInviteResponse {
  org: OrgInfo
  role: string
  message: string
}

// Legacy type for compatibility
export interface Organization {
  id: string
  slug: string
  name: string
  description: string | null
  logo_url: string | null
  owner_id: string
  member_count: number
  app_count: number
  created_at: string
}

// ============================================================================
// Types - Application
// ============================================================================

export interface AppSummaryInfo {
  id: string
  slug: string
  name: string
  logo_url: string | null
  banner_logo_url: string | null
  status: string
  created_at: string
}

export interface AppFullInfo {
  id: string
  slug: string
  name: string
  description: string | null
  client_id: string
  logo_url: string | null
  banner_logo_url: string | null
  website_url: string | null
  privacy_policy_url: string | null
  terms_of_service_url: string | null
  redirect_uris: string[]
  everyone_scopes: string[]
  status: string
  auto_authorize: boolean
  require_consent: boolean
  allow_pkce_plain: boolean
  created_at: string
  updated_at: string
}

export interface AppScopeInfo {
  id: string
  name: string
  description: string | null
  status: string
  created_at: string
}

export interface AppRoleInfo {
  id: string
  slug: string
  name: string
  description: string | null
  scopes: string[]
  is_system: boolean
  created_at: string
}

export interface AppUserInfo {
  id: string
  user_id: string
  email: string | null
  display_name: string | null
  role_slugs: string[]
  status: string
  consented_scopes: string[]
  last_consent_at: string | null
  created_at: string
  last_active_at: string | null
}

// ============================================================================
// Types - Webhooks
// ============================================================================

export interface WebhookInfo {
  id: string
  url: string
  events: string[]
  description: string | null
  status: 'active' | 'paused' | 'disabled'
  last_delivery_at: string | null
  last_response_code: number | null
  failure_count: number
  created_at: string
  updated_at: string
}

export interface WebhookWithSecret extends WebhookInfo {
  signing_secret: string
}

// ============================================================================
// Types - Billing
// ============================================================================

export interface BillingMeter {
  id: string
  slug: string
  name: string
  description: string | null
  meter_type: 'counter' | 'gauge' | 'histogram'
  aggregation: 'sum' | 'max' | 'min' | 'avg' | 'count' | 'last'
  unit: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface BillingStats {
  monthly_spend: number
  projected_spend: number
  active_apps: number
}

export interface BillingPlan {
  name: string
  price: number | string
  users: string
  apps: string
  current: boolean
}

export interface BillingPaymentMethod {
  brand: string
  last4: string
  exp_month: number
  exp_year: number
}

export interface BillingInvoice {
  id: string
  date: string
  amount: number
  status: string
  url: string | null
}

export interface OrgBillingInfo {
  stats: BillingStats
  plans: BillingPlan[]
  invoices: BillingInvoice[]
  payment_method: BillingPaymentMethod | null
  next_invoice_date: string | null
  // Additional platform details
  plan_tier: 'free' | 'pro' | 'enterprise'
  status: string
  current_mau: number
  mau_limit: number | null
  trial_ends_at: string | null
}

export interface OAuthProviderConfig {
  enabled: boolean
  client_id: string | null
  configured: boolean
}

export interface StripeConfig {
  enabled: boolean
  publishable_key: string | null
  connect_account_id: string | null
  configured: boolean
}

export interface PayPalConfig {
  enabled: boolean
  client_id: string | null
  merchant_id: string | null
  configured: boolean
}

export interface SubdomainConfig {
  core_subdomain: string | null
  api_subdomain: string | null
  account_subdomain: string | null
}

export interface SubdomainHostnameStatus {
  hostname: string
  status: string  // "pending", "active", "pending_validation", "failed", "not_found"
  ssl_status: string  // "initializing", "pending_validation", "pending_issuance", "active"
}

export interface SubdomainStatusResponse {
  provisioning_status: string  // "pending", "active", "failed"
  provisioning_error: string | null
  core: SubdomainHostnameStatus | null
  api: SubdomainHostnameStatus | null
  account: SubdomainHostnameStatus | null
}

export interface ReprovisionSubdomainsResponse {
  success: boolean
  message: string
  provisioning_status: string
}

export interface OrgConfigResponse {
  oauth: {
    google: OAuthProviderConfig
    github: OAuthProviderConfig
    discord: OAuthProviderConfig
    twitter: OAuthProviderConfig
    telegram: { enabled: boolean; configured: boolean }
  }
  stripe: StripeConfig
  paypal: PayPalConfig
  branding: Record<string, unknown> | null
  email_from_name: string | null
  email_from_address: string | null
  require_email_verification: boolean
  allow_registration: boolean
  subdomains: SubdomainConfig
}

// Legacy types for compatibility
export interface PaymentMethod {
  id: string
  type: string
  last4: string
  brand: string | null
  is_default: boolean
}

export interface SubscriptionInfo {
  id: string
  plan_name: string
  status: string
  current_period_end: string
}

// ============================================================================
// Types - Analytics
// ============================================================================

export interface AnalyticsData {
  total_authorizations: number
  active_authorizations: number
  total_api_keys: number
  active_api_keys: number
  requests_24h: number
  requests_7d: number
  requests_30d: number
  top_scopes: { scope: string; count: number }[]
  authorization_trend: { date: string; count: number }[]
}

export interface OrgDashboardStats {
  apps: number
  total_users: number
  api_calls_30d: number
  revenue_mtd_cents: number
}

export interface RecentActivity {
  id: string
  type: string
  description: string
  actor: string | null
  created_at: string
}

// ============================================================================
// API object with all endpoints
// ============================================================================

export const api = {
  // ==========================================================================
  // Auth
  // ==========================================================================
  auth: {
    login: (data: LoginData) =>
      post<LoginResponse>('/console/auth/login', data, { skipAuth: true }),
    register: (data: RegisterData) =>
      post<LoginResponse>('/console/auth/register', data, { skipAuth: true }),
    logout: () => post<void>('/console/auth/logout'),
    refresh: () => post<LoginResponse>('/console/auth/refresh'),
    verifyMFA: (data: MFAVerifyData) =>
      post<LoginResponse>('/console/auth/mfa/verify', data, { skipAuth: true }),
    socialLogin: (provider: string, code: string) =>
      post<LoginResponse>('/console/auth/social', { provider, code }, { skipAuth: true }),
  },

  // ==========================================================================
  // Current Developer (me)
  // ==========================================================================
  me: {
    get: () => get<Developer>('/console/auth/me'),
    update: (data: { display_name?: string; avatar_url?: string }) =>
      patch<Developer>('/console/auth/me', data),
  },

  // ==========================================================================
  // Password
  // ==========================================================================
  password: {
    change: (data: { current_password: string; new_password: string }) =>
      put<void>('/console/auth/password', data),
    set: (data: { password: string }) =>
      post<void>('/console/auth/password', data),
  },

  // ==========================================================================
  // Sessions
  // ==========================================================================
  sessions: {
    list: () => get<{ sessions: Session[] }>('/console/auth/sessions'),
    revoke: (sessionId: string) => del<void>(`/console/auth/sessions/${sessionId}`),
    revokeAll: () => post<void>('/console/auth/sessions/revoke-others'),
  },

  // ==========================================================================
  // MFA
  // ==========================================================================
  mfa: {
    status: () => get<{ enabled: boolean; has_totp: boolean; passkey_count: number }>('/console/auth/mfa/status'),
    list: () => get<{ passkeys: MFAFactor[] }>('/console/auth/passkeys'),
    beginTOTP: () => post<TOTPSetupResponse>('/console/auth/mfa/setup', { name: 'Authenticator' }),
    verifyTOTP: (code: string) =>
      post<{ recovery_codes: string[] }>('/console/auth/mfa/verify', { code }),
    disable: (code: string) => post<void>('/console/auth/mfa/disable', { code }),
    getRecoveryCodes: () => get<{ codes: string[] }>('/console/auth/mfa/recovery-codes'),
    regenerateRecoveryCodes: () =>
      post<{ codes: string[] }>('/console/auth/mfa/recovery-codes'),
  },

  // ==========================================================================
  // Identities (Developer OAuth identities)
  // ==========================================================================
  identities: {
    list: () => get<LinkedIdentity[]>('/console/auth/identities'),
    unlink: (identityId: string) => del<void>(`/console/auth/identities/${identityId}`),
    link: (provider: string, code: string) =>
      post<LinkedIdentity>('/console/auth/identities', { provider, code }),
  },

  // ==========================================================================
  // Organizations
  // ==========================================================================
  orgs: {
    // Backend currently returns 'organizations' key - will be 'orgs' after restart
    list: () => get<{ orgs?: ConsoleOrgWithRole[], organizations?: ConsoleOrgWithRole[] }>('/console/me/orgs'),
    get: (orgSlug: string) => get<OrgDetailResponse>(`/console/orgs/${orgSlug}`),
    create: (data: { name: string; slug: string; description?: string; logo_url?: string; website_url?: string }) =>
      post<{ org: OrgInfo }>('/console/me/orgs', data),
    update: (orgSlug: string, data: { name?: string; description?: string; logo_url?: string; website_url?: string }) =>
      patch<{ org: OrgInfo }>(`/console/orgs/${orgSlug}`, data),
    delete: (orgId: string) => del<void>(`/console/orgs/${orgId}`, { confirm: true }),

    // Team (Developers)
    listMembers: (orgId: string) =>
      get<{ developers: DeveloperInfo[] }>(`/console/orgs/${orgId}/developers`),
    inviteMember: (orgId: string, data: { email: string; role: string }) =>
      post<{ invite: InviteInfo }>(`/console/orgs/${orgId}/invites`, data),
    updateMemberRole: (orgId: string, roleId: string, role: string) =>
      patch<{ developer: DeveloperInfo }>(`/console/orgs/${orgId}/developers/${roleId}`, { role }),
    removeMember: (orgId: string, roleId: string) =>
      del<void>(`/console/orgs/${orgId}/developers/${roleId}`),
    leave: (orgId: string) =>
      post<void>(`/console/orgs/${orgId}/leave`),
    transferOwnership: (orgId: string, newOwnerUserId: string) =>
      post<void>(`/console/orgs/${orgId}/transfer-ownership`, { new_owner_user_id: newOwnerUserId }),

    // Invitations
    listInvitations: (orgId: string) =>
      get<{ invites: InviteInfo[] }>(`/console/orgs/${orgId}/invites`),
    cancelInvitation: (orgId: string, invitationId: string) =>
      del<void>(`/console/orgs/${orgId}/invites/${invitationId}`),
    resendInvitation: (orgId: string, invitationId: string) =>
      post<{ invite: InviteInfo; message: string }>(`/console/orgs/${orgId}/invites/${invitationId}/resend`),
    validateInvitation: (token: string) =>
      get<ValidateInviteResponse>(`/console/invites/validate/${token}`),
    acceptInvitation: (token: string) =>
      post<AcceptInviteResponse>('/console/invites/accept', { token }),

    // Roles
    listRoles: (orgId: string) =>
      get<{ roles: OrgRoleInfo[] }>(`/console/orgs/${orgId}/roles`),
    createRole: (orgId: string, data: { slug: string; name: string; description?: string; scopes: string[] }) =>
      post<{ role: OrgRoleInfo }>(`/console/orgs/${orgId}/roles`, data),
    updateRole: (orgId: string, roleId: string, data: { name?: string; description?: string; scopes?: string[] }) =>
      patch<{ role: OrgRoleInfo }>(`/console/orgs/${orgId}/roles/${roleId}`, data),
    deleteRole: (orgId: string, roleId: string) =>
      del<void>(`/console/orgs/${orgId}/roles/${roleId}`),

    // Dashboard/Stats
    getStats: (orgId: string) =>
      get<OrgDashboardStats>(`/console/orgs/${orgId}/stats`),
    getActivity: (orgId: string) =>
      get<{ activities: RecentActivity[] }>(`/console/orgs/${orgId}/activity`),

    // Billing
    getBilling: (orgId: string) =>
      get<OrgBillingInfo>(`/console/orgs/${orgId}/billing`),

    // Uploads
    uploadLogo: (orgSlug: string, file: File) =>
      uploadFile<{ url: string }>(`/console/orgs/${orgSlug}/upload/logo`, file),
    uploadBanner: (orgSlug: string, file: File) =>
      uploadFile<{ url: string }>(`/console/orgs/${orgSlug}/upload/banner`, file),

    // Config (OAuth, Stripe, etc.)
    getConfig: (orgSlug: string) =>
      get<OrgConfigResponse>(`/console/orgs/${orgSlug}/config`),
    setOAuthKeys: (orgSlug: string, provider: string, data: { client_id: string; client_secret: string }) =>
      put<void>(`/console/orgs/${orgSlug}/config/oauth/${provider}`, data),
    removeOAuthKeys: (orgSlug: string, provider: string) =>
      del<void>(`/console/orgs/${orgSlug}/config/oauth/${provider}`),
    setStripeKeys: (orgSlug: string, data: { publishable_key: string; secret_key: string; webhook_secret: string }) =>
      put<{ message: string; webhook_url: string; required_events: string[] }>(`/console/orgs/${orgSlug}/config/stripe`, data),
    removeStripeKeys: (orgSlug: string) =>
      del<void>(`/console/orgs/${orgSlug}/config/stripe`),

    // Custom subdomains
    updateSubdomains: (orgSlug: string, data: { core_subdomain?: string | null; api_subdomain?: string | null; account_subdomain?: string | null }) =>
      patch<SubdomainConfig>(`/console/orgs/${orgSlug}/config/subdomains`, data),
    getSubdomainStatus: (orgSlug: string) =>
      get<SubdomainStatusResponse>(`/console/orgs/${orgSlug}/config/subdomains/status`),
    reprovisionSubdomains: (orgSlug: string) =>
      post<ReprovisionSubdomainsResponse>(`/console/orgs/${orgSlug}/config/subdomains/reprovision`),
  },

  // ==========================================================================
  // Applications
  // ==========================================================================
  apps: {
    list: (orgId: string) =>
      get<{ apps: AppSummaryInfo[] }>(`/console/orgs/${orgId}/apps`),
    get: (orgId: string, appSlug: string) =>
      get<{ app: AppFullInfo; scope_count: number; role_count: number; webhook_count: number; authorization_count: number }>(`/console/orgs/${orgId}/apps/${appSlug}`),
    create: (orgId: string, data: {
      slug: string;
      name: string;
      description?: string;
      logo_url?: string;
      website_url?: string;
      redirect_uris?: string[];
    }) =>
      post<{ app: AppFullInfo & { client_secret: string }; message: string }>(`/console/orgs/${orgId}/apps`, data),
    update: (orgId: string, appId: string, data: {
      name?: string;
      description?: string;
      logo_url?: string;
      website_url?: string;
      privacy_policy_url?: string;
      terms_of_service_url?: string;
      redirect_uris?: string[];
      require_consent?: boolean;
      allow_pkce_plain?: boolean;
    }) =>
      patch<{ app: AppFullInfo; message: string }>(`/console/orgs/${orgId}/apps/${appId}`, data),
    delete: (orgId: string, appId: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}`, { confirm: true }),
    rotateSecret: (orgId: string, appId: string) =>
      post<{ client_secret: string }>(`/console/orgs/${orgId}/apps/${appId}/credentials/rotate-secret`),

    // Scopes
    listScopes: (orgId: string, appId: string, includeDeprecated = false) =>
      get<{ scopes: AppScopeInfo[] }>(`/console/orgs/${orgId}/apps/${appId}/scopes?include_deprecated=${includeDeprecated}`),
    createScope: (orgId: string, appId: string, data: { name: string; description?: string }) =>
      post<{ scope: AppScopeInfo }>(`/console/orgs/${orgId}/apps/${appId}/scopes`, data),
    updateScope: (orgId: string, appId: string, scopeName: string, description: string) =>
      patch<{ scope: AppScopeInfo }>(`/console/orgs/${orgId}/apps/${appId}/scopes/${scopeName}`, { description }),
    deprecateScope: (orgId: string, appId: string, scopeName: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}/scopes/${scopeName}`),

    // Roles
    listRoles: (orgId: string, appId: string) =>
      get<{ roles: AppRoleInfo[] }>(`/console/orgs/${orgId}/apps/${appId}/roles`),
    createRole: (orgId: string, appId: string, data: { slug: string; name: string; description?: string; scopes: string[] }) =>
      post<{ role: AppRoleInfo }>(`/console/orgs/${orgId}/apps/${appId}/roles`, data),
    updateRole: (orgId: string, appId: string, roleId: string, data: { name?: string; description?: string; scopes?: string[] }) =>
      patch<{ role: AppRoleInfo }>(`/console/orgs/${orgId}/apps/${appId}/roles/${roleId}`, data),
    deleteRole: (orgId: string, appId: string, roleId: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}/roles/${roleId}`),

    // @everyone
    getEveryone: (orgId: string, appId: string) =>
      get<{ scopes: string[] }>(`/console/orgs/${orgId}/apps/${appId}/everyone`),
    updateEveryone: (orgId: string, appId: string, scopes: string[]) =>
      put<{ scopes: string[] }>(`/console/orgs/${orgId}/apps/${appId}/everyone`, { scopes }),

    // Users
    listUsers: (orgId: string, appId: string, page = 1, perPage = 50) =>
      get<{ users: AppUserInfo[]; total: number; page: number; per_page: number }>(`/console/orgs/${orgId}/apps/${appId}/users?page=${page}&per_page=${perPage}`),
    getUser: (orgId: string, appId: string, userId: string) =>
      get<{ user: AppUserInfo }>(`/console/orgs/${orgId}/apps/${appId}/users/${userId}`),
    assignRoles: (orgId: string, appId: string, userId: string, roleSlugs: string[]) =>
      patch<{ user: AppUserInfo }>(`/console/orgs/${orgId}/apps/${appId}/users/${userId}/roles`, { role_slugs: roleSlugs }),
    removeUser: (orgId: string, appId: string, userId: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}/users/${userId}`),

    // Analytics
    getAnalytics: (orgId: string, appId: string) =>
      get<AnalyticsData>(`/console/orgs/${orgId}/apps/${appId}/analytics`),

    // Webhooks
    listWebhooks: (orgId: string, appId: string) =>
      get<{ webhooks: WebhookInfo[] }>(`/console/orgs/${orgId}/apps/${appId}/webhooks`),
    createWebhook: (orgId: string, appId: string, data: { url: string; events: string[]; description?: string }) =>
      post<{ webhook: WebhookWithSecret }>(`/console/orgs/${orgId}/apps/${appId}/webhooks`, data),
    updateWebhook: (orgId: string, appId: string, webhookId: string, data: { url?: string; events?: string[]; status?: string }) =>
      patch<{ webhook: WebhookInfo }>(`/console/orgs/${orgId}/apps/${appId}/webhooks/${webhookId}`, data),
    deleteWebhook: (orgId: string, appId: string, webhookId: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}/webhooks/${webhookId}`),
    rotateWebhookSecret: (orgId: string, appId: string, webhookId: string) =>
      post<{ secret: string }>(`/console/orgs/${orgId}/apps/${appId}/webhooks/${webhookId}/rotate-secret`),

    // Meters (Billing)
    listMeters: (orgId: string, appId: string) =>
      get<{ meters: BillingMeter[] }>(`/console/orgs/${orgId}/apps/${appId}/meters`),
    createMeter: (orgId: string, appId: string, data: { name: string; description?: string; unit: string; rate_cents: number }) =>
      post<{ meter: BillingMeter }>(`/console/orgs/${orgId}/apps/${appId}/meters`, data),
    updateMeter: (orgId: string, appId: string, meterId: string, data: { name?: string; description?: string; rate_cents?: number }) =>
      patch<{ meter: BillingMeter }>(`/console/orgs/${orgId}/apps/${appId}/meters/${meterId}`, data),
    deleteMeter: (orgId: string, appId: string, meterId: string) =>
      del<void>(`/console/orgs/${orgId}/apps/${appId}/meters/${meterId}`),

    // Uploads
    uploadIcon: (orgSlug: string, appSlug: string, file: File) =>
      uploadFile<{ url: string }>(`/console/orgs/${orgSlug}/apps/${appSlug}/upload/icon`, file),
    uploadBanner: (orgSlug: string, appSlug: string, file: File) =>
      uploadFile<{ url: string }>(`/console/orgs/${orgSlug}/apps/${appSlug}/upload/banner`, file),
  },

  // ==========================================================================
  // Available Scopes
  // ==========================================================================
  scopes: {
    getOrgScopes: () => get<{ scopes: string[] }>('/console/scopes'),
  },
}
